import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  Paper,
  IconButton,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import DialpadIcon from "@mui/icons-material/Dialpad";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useCallContext } from "../context/CallContext";
import useDraggablePanel from "../hooks/useDraggablePanel";
import {
  startIncomingCallAttention,
  stopIncomingCallAttention,
} from "../utils/incomingCallAttention";
import {
  isCallPopupShell,
  publishCallAction,
  requestCallSessionSync,
  subscribeCallSessionBridge,
} from "../utils/callSessionBridge";
import { isMobileTeamViewport } from "../utils/teamNavigation";

const DTMF_KEYS = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

const CallNotification = () => {
  const {
    incomingCall,
    acceptCall,
    rejectCall,
    outgoingCall,
    endOutgoingCall,
    sendOutgoingDtmf,
    lastCallNotice,
    clearLastCallNotice,
  } = useCallContext();

  const isMobileUi = useMediaQuery("(max-width: 768px)");
  const inPopupShell = isCallPopupShell();
  const [remoteOutgoing, setRemoteOutgoing] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [showDialpad, setShowDialpad] = useState(true);
  const [dtmfBuffer, setDtmfBuffer] = useState("");
  const incomingDrag = useDraggablePanel("xpro-call-incoming-pos", {
    x: Math.max(8, (typeof window !== "undefined" ? window.innerWidth : 400) - 380),
    y: 16,
  });
  const activeDrag = useDraggablePanel("xpro-call-active-pos", {
    x: Math.max(8, (typeof window !== "undefined" ? window.innerWidth : 400) - 380),
    y: 16,
  });

  useEffect(() => {
    if (!inPopupShell) {
      setRemoteOutgoing(null);
      return undefined;
    }
    requestCallSessionSync();
    return subscribeCallSessionBridge((msg) => {
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "cleared") {
        setRemoteOutgoing(null);
        return;
      }
      if (msg.type === "session") {
        setRemoteOutgoing(msg.session || null);
      }
    });
  }, [inPopupShell]);

  const localOutgoing = outgoingCall || null;
  const hideLocalOnOpener = Boolean(localOutgoing?.uiInPopup) && !inPopupShell;

  const activeOutgoing = (() => {
    if (inPopupShell) {
      if (localOutgoing) return { ...localOutgoing, __remote: false };
      if (remoteOutgoing) return { ...remoteOutgoing, __remote: true };
      return null;
    }
    if (hideLocalOnOpener) return null;
    return localOutgoing ? { ...localOutgoing, __remote: false } : null;
  })();

  const conversation = incomingCall?.conversation;
  const parameters = incomingCall?.parameters;
  const isGupshupSipCall = incomingCall?.kind === "whatsapp_gupshup_sip";
  const callDetails = {
    name: conversation?.name ?? parameters?.From ?? "Chamador",
    phoneNumber: parameters?.From ?? conversation?.phoneNumber ?? conversation?.name ?? "—",
    protocol: parameters?.Protocol ?? conversation?.id ?? conversation?.protocol ?? "—",
    channel: conversation?.channel ?? "Telefone",
    status: conversation?.status ?? "Pendente",
    assignedTo: conversation?.assignedTo ?? "—",
    icon: <PhoneIcon fontSize="small" />,
  };

  const canSendDtmf = Boolean(
    activeOutgoing &&
      (activeOutgoing.__remote
        ? activeOutgoing.canDtmf
        : Boolean(activeOutgoing?.callInstance) || activeOutgoing?.mode === "mock")
  );

  useEffect(() => {
    if (incomingCall) startIncomingCallAttention();
    else stopIncomingCallAttention();
    return () => stopIncomingCallAttention();
  }, [incomingCall]);

  useEffect(() => {
    let interval = null;
    setCallDuration(0);
    setShowDialpad(Boolean(canSendDtmf));
    setDtmfBuffer("");
    if (activeOutgoing) {
      const startedAt = activeOutgoing.startTime
        ? new Date(activeOutgoing.startTime).getTime()
        : Date.now();
      const tick = () => {
        const sec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
        setCallDuration(sec);
      };
      tick();
      interval = setInterval(tick, 1000);
    }
    return () => clearInterval(interval);
  }, [activeOutgoing?.callSid, activeOutgoing?.startTime, activeOutgoing?.toNumber, canSendDtmf]);

  const pressDtmf = useCallback(
    (digit) => {
      if (!canSendDtmf) return;
      const d = String(digit);
      if (activeOutgoing?.__remote) {
        publishCallAction("dtmf", { digits: d });
        setDtmfBuffer((prev) => `${prev}${d}`.slice(-24));
        return;
      }
      const result = sendOutgoingDtmf?.(d);
      if (result?.ok) {
        setDtmfBuffer((prev) => `${prev}${d}`.slice(-24));
      }
    },
    [canSendDtmf, activeOutgoing?.__remote, sendOutgoingDtmf]
  );

  useEffect(() => {
    if (!showDialpad || !canSendDtmf) return undefined;
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key;
      if (/^[0-9*#]$/.test(key)) {
        e.preventDefault();
        pressDtmf(key);
      } else if (key === "Backspace") {
        e.preventDefault();
        setDtmfBuffer((prev) => prev.slice(0, -1));
      } else if (key === "Escape") {
        setShowDialpad(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showDialpad, canSendDtmf, pressDtmf]);

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const handleAcceptCall = async () => {
    stopIncomingCallAttention();
    await acceptCall();
  };

  const handleRejectCall = async () => {
    stopIncomingCallAttention();
    await rejectCall();
  };

  const handleHangup = () => {
    if (activeOutgoing?.__remote) {
      publishCallAction("hangup");
      setRemoteOutgoing(null);
      return;
    }
    endOutgoingCall();
  };

  const destinationLabel =
    activeOutgoing?.toNumber || callDetails.phoneNumber || "Ligação";
  const isClickToCall =
    activeOutgoing?.mode === "twilio_pstn" ||
    activeOutgoing?.bridgeMode === "click_to_call";
  const originLabel = activeOutgoing?.fromNumber || null;

  if (!incomingCall && !activeOutgoing) return null;

  const useMobileSheet = isMobileUi || (typeof window !== "undefined" && isMobileTeamViewport());

  const mobileSheetSx = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1400,
    padding: 2,
    paddingBottom: "max(16px, env(safe-area-inset-bottom))",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#1a1a1a",
    color: "#fff",
    borderRadius: "16px 16px 0 0",
    maxHeight: "85vh",
    overflow: "auto",
  };

  const dragHandleSx = {
    display: useMobileSheet ? "none" : "flex",
    alignItems: "center",
    gap: 0.75,
    mb: 1.25,
    mx: -0.5,
    px: 0.5,
    py: 0.25,
    borderRadius: 1,
    cursor: "grab",
    userSelect: "none",
    opacity: 0.9,
    "&:active": { cursor: "grabbing" },
  };

  const desktopIncomingSx = {
    position: "fixed",
    left: incomingDrag.position.x,
    top: incomingDrag.position.y,
    zIndex: 1400,
    padding: 2,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#333",
    color: "#fff",
    borderRadius: 2,
    minWidth: 350,
    maxWidth: 400,
    cursor: incomingDrag.dragging ? "grabbing" : "default",
  };

  const desktopActiveSx = {
    position: "fixed",
    left: activeDrag.position.x,
    top: activeDrag.position.y,
    zIndex: 1400,
    padding: 2,
    backgroundColor: "#333",
    color: "#fff",
    borderRadius: 2,
    minWidth: showDialpad ? 300 : 350,
    maxWidth: 360,
    cursor: activeDrag.dragging ? "grabbing" : "default",
  };

  return (
    <>
      {incomingCall && (
        <Paper
          ref={useMobileSheet ? undefined : incomingDrag.panelRef}
          elevation={3}
          sx={useMobileSheet ? mobileSheetSx : desktopIncomingSx}
        >
          {!useMobileSheet ? (
            <Box
              sx={dragHandleSx}
              onMouseDown={incomingDrag.onDragHandleMouseDown}
              onTouchStart={incomingDrag.onDragHandleTouchStart}
            >
              <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.7 }} />
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Arrastar
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: 40,
                height: 4,
                bgcolor: "rgba(255,255,255,0.35)",
                borderRadius: 2,
                mx: "auto",
                mb: 1.5,
              }}
            />
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexDirection: useMobileSheet ? "column" : "row",
            }}
          >
            <Avatar
              sx={{
                backgroundColor: "#00796B",
                width: useMobileSheet ? 72 : 48,
                height: useMobileSheet ? 72 : 48,
              }}
            >
              {callDetails.name[0]}
            </Avatar>

            <Box
              flexGrow={1}
              minWidth={0}
              sx={{ textAlign: useMobileSheet ? "center" : "left", width: "100%" }}
            >
              <Typography variant={useMobileSheet ? "h6" : "subtitle1"} fontWeight="bold" noWrap>
                {callDetails.phoneNumber && callDetails.phoneNumber !== "—"
                  ? callDetails.phoneNumber
                  : callDetails.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                Ligação entrando
              </Typography>
              {isGupshupSipCall && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    opacity: 0.9,
                    maxWidth: useMobileSheet ? "100%" : 280,
                  }}
                >
                  Modo SIP (Gupshup): o áudio não passa pelo navegador. Atenda pelo telefone/SBC
                  configurado para este canal.
                </Typography>
              )}
              {callDetails.protocol && callDetails.protocol !== "—" ? (
                <Typography variant="caption" sx={{ opacity: 0.8 }} noWrap display="block">
                  Solicitação {callDetails.protocol}
                </Typography>
              ) : null}
            </Box>

            <Box
              display="flex"
              flexDirection={useMobileSheet ? "row" : "column"}
              gap={1}
              sx={{ width: useMobileSheet ? "100%" : "auto", mt: useMobileSheet ? 1 : 0 }}
            >
              <Button
                size={useMobileSheet ? "large" : "small"}
                variant="contained"
                color="success"
                onClick={handleAcceptCall}
                fullWidth={useMobileSheet}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  flex: useMobileSheet ? 1 : undefined,
                }}
              >
                Atender
              </Button>
              <Button
                size={useMobileSheet ? "large" : "small"}
                variant="outlined"
                color="inherit"
                onClick={handleRejectCall}
                fullWidth={useMobileSheet}
                sx={{
                  textTransform: "none",
                  borderColor: "rgba(255,255,255,0.35)",
                  flex: useMobileSheet ? 1 : undefined,
                }}
              >
                Recusar
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {activeOutgoing && (
        <Paper
          ref={useMobileSheet ? undefined : activeDrag.panelRef}
          elevation={3}
          sx={useMobileSheet ? mobileSheetSx : desktopActiveSx}
        >
          {!useMobileSheet ? (
            <Box
              sx={dragHandleSx}
              onMouseDown={activeDrag.onDragHandleMouseDown}
              onTouchStart={activeDrag.onDragHandleTouchStart}
            >
              <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.7 }} />
              <Typography variant="caption" sx={{ opacity: 0.75 }}>
                Arrastar
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: 40,
                height: 4,
                bgcolor: "rgba(255,255,255,0.35)",
                borderRadius: 2,
                mx: "auto",
                mb: 1.5,
              }}
            />
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Avatar sx={{ backgroundColor: "#00796B", width: 48, height: 48 }}>
              {String(destinationLabel).replace(/\D/g, "").slice(-1) || "C"}
            </Avatar>
            <Box flexGrow={1} minWidth={0}>
              <Typography variant="subtitle1" fontWeight="bold" noWrap>
                {isClickToCall ? "Ligação via telefone" : "Ligação em andamento"}
              </Typography>
              {isClickToCall && originLabel ? (
                <Typography variant="caption" sx={{ opacity: 0.85, display: "block" }} noWrap>
                  Origem: {originLabel}
                </Typography>
              ) : null}
              <Typography variant="caption" sx={{ opacity: 0.85, display: "block" }} noWrap>
                {isClickToCall ? `Destino: ${destinationLabel}` : destinationLabel}
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="success.main">
                Duração: {formatDuration(callDuration)}
              </Typography>
              {!useMobileSheet ? (
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.75, opacity: 0.85, lineHeight: 1.35 }}
                >
                  {isClickToCall
                    ? "Toca no seu telefone primeiro; ao atender, conectamos o destino. Desligar aqui encerra as duas pernas."
                    : "Não atualize nem feche a aba do softphone — a ligação cai para o cliente."}
                </Typography>
              ) : null}
            </Box>
            {canSendDtmf ? (
              <Tooltip title={showDialpad ? "Ocultar teclado" : "Teclado (URA)"}>
                <IconButton
                  size="small"
                  onClick={() => setShowDialpad((v) => !v)}
                  aria-label="Teclado numérico"
                  sx={{
                    color: "#fff",
                    bgcolor: showDialpad ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.08)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.22)" },
                  }}
                >
                  <DialpadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
            <Button
              size={useMobileSheet ? "large" : "small"}
              variant="contained"
              color="error"
              onClick={handleHangup}
              fullWidth={useMobileSheet}
              sx={{ textTransform: "none", ...(useMobileSheet ? { mt: 1 } : {}) }}
            >
              Desligar
            </Button>
          </Box>

          {showDialpad && canSendDtmf ? (
            <Box sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                  px: 0.5,
                  minHeight: 28,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                    letterSpacing: 2,
                    opacity: dtmfBuffer ? 1 : 0.5,
                  }}
                >
                  {dtmfBuffer || "Teclado URA"}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setDtmfBuffer((prev) => prev.slice(0, -1))}
                  disabled={!dtmfBuffer}
                  aria-label="Apagar"
                  sx={{ color: "#fff", opacity: dtmfBuffer ? 0.9 : 0.35 }}
                >
                  <BackspaceOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: 1,
                }}
              >
                {DTMF_KEYS.flat().map((digit) => (
                  <Button
                    key={digit}
                    variant="contained"
                    onClick={() => pressDtmf(digit)}
                    sx={{
                      minWidth: 0,
                      py: useMobileSheet ? 1.75 : 1.25,
                      fontSize: "1.15rem",
                      fontWeight: 700,
                      textTransform: "none",
                      bgcolor: "rgba(255,255,255,0.12)",
                      color: "#fff",
                      boxShadow: "none",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.22)", boxShadow: "none" },
                    }}
                  >
                    {digit}
                  </Button>
                ))}
              </Box>
            </Box>
          ) : null}

          {!canSendDtmf && activeOutgoing?.mode === "pabx" ? (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.7 }}>
              Ligação via PABX: use o teclado do ramal/aparelho para a URA.
            </Typography>
          ) : null}
          {!canSendDtmf && isClickToCall ? (
            <Typography variant="caption" sx={{ display: "block", mt: 1.5, opacity: 0.7 }}>
              Áudio no telefone de origem. URA: use o teclado do aparelho.
            </Typography>
          ) : null}
        </Paper>
      )}

      {!activeOutgoing && lastCallNotice ? (
        <Paper
          elevation={3}
          sx={{
            position: "fixed",
            right: 16,
            top: 16,
            zIndex: 1400,
            p: 2,
            maxWidth: 360,
            bgcolor: "#b71c1c",
            color: "#fff",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700}>
            Ligação não concluída
          </Typography>
          <Typography variant="caption" sx={{ display: "block", opacity: 0.9, mt: 0.5 }}>
            {lastCallNotice.toNumber ? `${lastCallNotice.toNumber} · ` : ""}
            {lastCallNotice.reason || "Não foi possível completar a ligação."}
          </Typography>
          <Button
            size="small"
            color="inherit"
            onClick={() => clearLastCallNotice?.()}
            sx={{ mt: 1, textTransform: "none" }}
          >
            Fechar
          </Button>
        </Paper>
      ) : null}
    </>
  );
};

export default CallNotification;
